import React from 'react';

/**
 * Paginator — reused by every admin list (Members / Achievements / Forum
 * logs / Blogs / Resources / Events / Recruitment / Training / Contributors
 * / Screening etc.). Rendered as two independent slots so the dropdown
 * sits above the list and the page nav sits below it:
 *
 *   <Paginator position="top"  ... />   ← size dropdown + "Hiển thị:" label
 *   {items.map(...)}
 *   <Paginator position="bottom" ... /> ← page number buttons
 *
 * Behaviour rules:
 *   - The TOP slot is hidden entirely when `total <= smallest size` (default
 *     10). No point showing a size picker if everything already fits.
 *   - The BOTTOM slot is hidden entirely when there is only one page of
 *     results — nothing to navigate between.
 *   - When `pageSize === 0` (Tất cả) we render the whole list and the
 *     bottom slot is hidden.
 */
const DEFAULT_SIZES = [10, 25, 50, 100, 0];

export default function Paginator({
  total = 0,
  page = 1,
  pageSize = 10,
  onChange,
  sizes = DEFAULT_SIZES,
  label = 'Hiển thị',
  position = 'both'
}) {
  const ps = pageSize === 0 ? total : pageSize;
  const totalPages = ps === 0 ? 1 : Math.max(1, Math.ceil(total / ps));
  const safePage = Math.min(page, totalPages);

  const setSize = (sz) => onChange && onChange({ page: 1, pageSize: sz });
  const setPage = (p) => {
    const next = Math.max(1, Math.min(totalPages, p));
    if (next !== safePage) onChange && onChange({ page: next, pageSize });
  };

  // Smallest positive size among the options — below that we hide the
  // size picker entirely because the list already fits on one page.
  const smallest = sizes.filter(s => s > 0).reduce((a, b) => Math.min(a, b), Infinity);
  const showTop = total > smallest;
  const showBottom = totalPages > 1;

  // ── TOP: page-size dropdown ──
  if (position === 'top') {
    if (!showTop) return null;
    return (
      <div className="paginator paginator-top">
        <label className="paginator-sizes">
          <span className="paginator-label">{label}:</span>
          <select
            className="paginator-select"
            value={pageSize}
            onChange={(e) => setSize(parseInt(e.target.value, 10))}
          >
            {sizes.map(sz => (
              <option key={sz} value={sz}>{sz === 0 ? 'Tất cả' : sz}</option>
            ))}
          </select>
        </label>
        <div className="paginator-info">
          <strong>{total}</strong> mục
        </div>
      </div>
    );
  }

  // ── BOTTOM: page number nav ──
  if (position === 'bottom') {
    if (!showBottom) return null;
    // Condensed list: 1, current ±2, last, with ellipses for the gaps.
    const keep = new Set([1, totalPages, safePage, safePage - 1, safePage + 1, safePage - 2, safePage + 2]);
    const list = [...keep].filter(n => n >= 1 && n <= totalPages).sort((a, b) => a - b);
    return (
      <div className="paginator paginator-bottom">
        <div className="paginator-nav">
          <button type="button" className="paginator-page" onClick={() => setPage(safePage - 1)} disabled={safePage <= 1}>‹</button>
          {list.map((n, i) => (
            <React.Fragment key={n}>
              {i > 0 && n - list[i - 1] > 1 && <span className="paginator-gap">…</span>}
              <button
                type="button"
                className={`paginator-page ${n === safePage ? 'active' : ''}`}
                onClick={() => setPage(n)}
              >{n}</button>
            </React.Fragment>
          ))}
          <button type="button" className="paginator-page" onClick={() => setPage(safePage + 1)} disabled={safePage >= totalPages}>›</button>
        </div>
        <div className="paginator-info">Trang {safePage} / {totalPages}</div>
      </div>
    );
  }

  // ── Legacy "both" mode (kept for callers that still render a single
  // <Paginator />). Renders top + bottom in a single row.
  return (
    <>
      <Paginator total={total} page={page} pageSize={pageSize} onChange={onChange} sizes={sizes} label={label} position="top" />
      <Paginator total={total} page={page} pageSize={pageSize} onChange={onChange} sizes={sizes} label={label} position="bottom" />
    </>
  );
}

/**
 * Helper hook — encapsulates the slice & state management so callers
 * can do `const { paged, controls } = usePaginator(items)`.
 */
export function usePaginator(items, defaultPageSize = 10) {
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(defaultPageSize);
  const total = (items || []).length;
  const ps = pageSize === 0 ? total : pageSize;
  const totalPages = ps === 0 ? 1 : Math.max(1, Math.ceil(total / ps));
  React.useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  const paged = pageSize === 0 ? (items || []) : (items || []).slice((page - 1) * pageSize, page * pageSize);
  const onChange = ({ page: p, pageSize: sz }) => {
    if (sz !== undefined) setPageSize(sz);
    if (p !== undefined) setPage(p);
  };
  return { paged, page, pageSize, total, onChange };
}
